import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit';
import { addMiddleware } from 'redux-dynamic-middlewares';
import { store } from '@redux/store';
import onListViewPaint from 'pages/searchResults/redux/actions';
import uiStateSlice from 'layout/redux/uiStateSlice';
import { userProfileSlice } from './slices';
import { favoritesSessionStorageKey, pendingSavesSessionStorageKey } from '../constants/sessionStorageConstants';
import { getUserPrefillFormData, selectFavorites, selectIsLoggedIn } from './selectors';
import updateSavedFavorites from '../components/favorites';
import { getLocalStoragePrefillFormData, prefillForm, updateUserInformation } from '../components/prefillForm';

const actions = userProfileSlice.actions;
const uiActions = uiStateSlice.actions;
const listenerMiddleware = createListenerMiddleware();

// update the favorites data on session storage.
// this is done when a save / delete is successful.
listenerMiddleware.startListening({
  matcher: isAnyOf(actions.deleteFavoriteFullfilled, actions.saveFavoriteFulfilled),
  effect: async () => {
    const state = store.getState();
    const { favorites } = state.userProfile;
    sessionStorage.setItem(favoritesSessionStorageKey, JSON.stringify(favorites));
  },
});

// update the pending saves data on session storage.
// this is done when a save / delete hasn't been completed yet.
listenerMiddleware.startListening({
  matcher: isAnyOf(actions.saveFavorite, actions.deleteFavorite, actions.saveFavoriteFulfilled),
  effect: async () => {
    const state = store.getState();
    const { pendingSaves } = state.userProfile;

    sessionStorage.setItem(pendingSavesSessionStorageKey, JSON.stringify(pendingSaves));
  },
});

/* When the list view is paint, update the favorites if the user is login. */
listenerMiddleware.startListening({
  matcher: isAnyOf(onListViewPaint),
  effect: async () => {
    const state = store.getState();
    const isLoggedIn = selectIsLoggedIn(state);
    if (isLoggedIn) {
      const favorites = selectFavorites(state);
      updateSavedFavorites(favorites);
    }
  },
});

/**
 * Retrieves or generates a cryptographic AES-GCM key for encryption and decryption.
 *
 * If a key is already stored in localStorage under the specified name, it imports the key using the Web Crypto API.
 * Otherwise, it generates a new 256-bit AES-GCM key.
 *
 * @param {boolean} isKeyStored - Indicates whether the key is already stored in localStorage.
 * @param {string} cryptoKeyName - The localStorage key name where the JWK-formatted key is stored.
 * @returns {Promise<CryptoKey>} A promise that resolves to the imported or newly generated CryptoKey.
 */
listenerMiddleware.startListening({
  matcher: isAnyOf(actions.update),
  effect: async () => {
    const userInformation = getUserPrefillFormData(store.getState());
    const storedUserInformation = await getLocalStoragePrefillFormData();
    const mergedUserInformation = {
      ...storedUserInformation,
      ...Object.fromEntries(
        Object.entries(userInformation).map(([key, value]) => {
          const storedValue = storedUserInformation?.[key];

          if (userInformation.livesOutside === true && key === "zip") {
            return [key, ""];
          }

          return [
            key,
            value !== "" ? value : storedValue ?? ""
          ];
        })
      )
    };

    await updateUserInformation(mergedUserInformation);
  },
});

/**
 * Listener that decrypts stored user information and prefills the lead form when the 'leadGenerator' modal opens.
 *
 * When the 'leadGenerator' modal is triggered, this listener attempts to retrieve an encrypted user information object
 * and a cryptographic key from localStorage. If both are present, it uses the key to decrypt the user information
 * and prefill the lead form fields with the decrypted data.
 *
 * The cryptographic key is expected to be stored in JWK (JSON Web Key) format under the name
 * specified by `cryptoKeyName`.
 * Decryption uses the AES-GCM algorithm via the Web Crypto API.
 */
listenerMiddleware.startListening({
  matcher: isAnyOf(uiActions.modalInformation),
  effect: async () => {
    await prefillForm();
  },
});

addMiddleware(listenerMiddleware.middleware);